import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Yii } from "libraries/interfaces/Yii";
import {
    ITraveller,
} from "libraries/interfaces/watabilight/IWatabiLightBooking.interface";
import { FormControl, FormGroup } from "@angular/forms";
import {
    IDateFieldSpecs,
    IFieldSpecs
} from "libraries/module/formbuilder/fields/interfaces/field-specs";
import {
    ISimpleSelectFieldConfig, ISimpleSelectOption
} from "libraries/module/formbuilder/fields/simple-select/simple-select.interface";
import { IWatabiLightSectionDetails } from "libraries/interfaces/IWenji";

class Wording {
    public readonly title: string = Yii.t('wui-shared-js', 'Voyageurs');
    public readonly name: string = Yii.t('wui-shared-js', 'Nom(s) et prénom(s)');
    public readonly type: string = Yii.t('wui-shared-js', 'Type');
    public readonly dateOfBirth: string = Yii.t('wui-shared-js', 'Date de naissance');
    public readonly undefined: string = Yii.t('wui-shared-js', 'Non précisé');
}

interface IAllTravellersConfig {
    [travellerId: string]: ITravellerConfig;
}

interface ITravellerConfig {
    titleConfig: ISimpleSelectFieldConfig;
    firstNameConfig: IFieldSpecs;
    lastNameConfig: IFieldSpecs;
    birthDateConfig: IDateFieldSpecs;
}

export interface IAllTravellersValue {
    [travellerId: string]: ITravellerValue;
}

export interface ITravellerValue {
    title: string;
    type: string;
    firstName: string;
    lastName: string;
    birthDate: Date;
}

enum EFormControlName {
    title = "title",
    firstName = "firstName",
    lastName = "lastName",
    birthDate = "birthDate",
    type = "type"
}

@Component({
    selector: 'watabilight-traveller',
    templateUrl: './traveller.component.html',
    styleUrls: [
        '../../booking.component.scss',
        './traveller.component.scss'
    ]
})
export class TravellerComponent implements OnInit {
    @Input()
    public travellers: ITraveller[];
    @Input()
    public form: FormGroup;
    @Input()
    public sectionConfig: IWatabiLightSectionDetails;
    @Input()
    public isEditable: boolean;

    public title: string;
    public description: string;


    @Output()
    public onEdit: EventEmitter<number> = new EventEmitter();

    public wording: Wording = new Wording();
    public isEdit: boolean = false;
    public withBorder: boolean = true;

    public config: IAllTravellersConfig = {};
    public formValue: IAllTravellersValue = {};

    public titleSelection: ISimpleSelectOption[] = [
        { label: Yii.t('wui-shared-js', 'M'), value: 'Mr' },
        { label: Yii.t('wui-shared-js', 'Mme'), value: 'Ms' },
    ];

    constructor(
        private readonly cdf: ChangeDetectorRef
    ) {
    }

    public ngOnInit(): void {
        this.title = this.sectionConfig?.title ? this.sectionConfig?.title : this.wording.title;
        this.description = this.sectionConfig?.description ? this.sectionConfig?.description : "";
        this.initFormControl();
        this.initFormFieldConfig();
        this.setTravellerValue();
        this.patchValueFromForm();

        this.onEdit.emit(2);
    }

    public update(): void {
        this.cdf.markForCheck();
    }

    public edit(): void {
        if (this.isEdit) {
            this.onEdit.emit(2);
            this.patchValueFromForm();
        } else {
            this.onEdit.emit(5);
        }
        this.isEdit = !this.isEdit;
    }

    public initFormControl(): void {
        this.travellers?.forEach((traveller: ITraveller) => {
            this.form.addControl(traveller.id, new FormGroup({
                [EFormControlName.title]: new FormControl(),
                [EFormControlName.firstName]: new FormControl(),
                [EFormControlName.lastName]: new FormControl(),
                [EFormControlName.birthDate]: new FormControl(),
                [EFormControlName.type]: new FormControl(),
            }));
        });
    }

    public initFormFieldConfig(): void {
        this.travellers?.forEach((traveller: ITraveller) => {
            const travellerForm: FormGroup = this.form.controls[traveller.id] as FormGroup;
            this.config[traveller.id] = {
                titleConfig: this.getSimpleSelectConfig(travellerForm, EFormControlName.title),
                firstNameConfig: this.getFieldConfig(travellerForm, EFormControlName.firstName),
                lastNameConfig: this.getFieldConfig(travellerForm, EFormControlName.lastName),
                birthDateConfig: this.getDateFieldConfig(travellerForm, EFormControlName.birthDate),
            };
        });
    }

    public getFieldConfig(travellerForm: FormGroup, fomControlName: string): IFieldSpecs {
        return {
            formGroup: travellerForm,
            formControlName: fomControlName,
            className: "watabiLightInput"
        };
    }

    public getSimpleSelectConfig(travellerForm: FormGroup, fomControlName: string): ISimpleSelectFieldConfig {
        return {
            formGroup: travellerForm,
            formControlName: fomControlName,
            className: "with-border",
            options: this.titleSelection
        };
    }

    public getDateFieldConfig(travellerForm: FormGroup, fomControlName: string): IDateFieldSpecs {
        return {
            formGroup: travellerForm,
            formControlName: fomControlName,
            className: "with-border",
            maxDate: new Date(),
            forceYearInPast: true
        };
    }

    public patchValueFromForm(): void {
        this.formValue = this.form.value;
        Object.keys(this.formValue).forEach((key: string) => {
            this.formValue[key].birthDate = this.makeDate(this.formValue[key].birthDate);
        });
    }

    public setTravellerValue(): void {
        this.travellers?.forEach((traveller: ITraveller) => {
            const travellerForm: FormGroup = this.form.controls[traveller.id] as FormGroup;
            this.patchValueIfExist(travellerForm, traveller.title, EFormControlName.title);
            this.patchValueIfExist(travellerForm, traveller.firstName, EFormControlName.firstName);
            this.patchValueIfExist(travellerForm, traveller.lastName, EFormControlName.lastName);
            this.patchDateValueIfExist(travellerForm, traveller.birthDate, EFormControlName.birthDate);
            this.patchValueIfExist(travellerForm, traveller.type.code, EFormControlName.type);
        });
    }

    public makeDate(val: string | Date): Date {
        if (val) {
            return new Date(val);
        }
        return (null);
    }

    public patchValueIfExist(travellerForm: FormGroup, value: string, key: string): void {
        if (value) {
            travellerForm.get(key).setValue(value);
        }
    }

    public patchDateValueIfExist(travellerForm: FormGroup, value: string, key: string): void {
        if (value) {
            travellerForm.get(key).setValue(new Date(value));
        }
    }

    public editTraveller(): void {
        this.onEdit.emit(2);
    }
}
